if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.videoTriggers = document.querySelectorAll(
          `[data-product-video-trigger][data-gallery-id="${this.id}"]`
        );
        this.videoTriggers.forEach((trigger) => {
          trigger.addEventListener('click', this.onVideoTrigger.bind(this));
        });
        this.videoMilestones = new WeakMap();
        this.addEventListener('click', this.onMediaClick.bind(this));

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      onVideoTrigger(event) {
        this.setActiveMedia(event.currentTarget.dataset.mediaId, false);
      }

      onMediaClick(event) {
        const deferredMedia = event.target.closest('deferred-media[data-media-id]');
        if (!deferredMedia || !this.contains(deferredMedia)) return;

        window.setTimeout(() => {
          const video = deferredMedia.querySelector('video');
          if (!video) return;
          this.trackVideo(video, `${this.id.replace('MediaGallery-', '')}-${deferredMedia.dataset.mediaId}`);
        });
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (!deferredMedia) return;

        deferredMedia.loadContent(false);
        const video = deferredMedia.querySelector('video');
        if (!video) return;

        this.trackVideo(video, activeItem.dataset.mediaId);
        video.play().catch(() => {});
      }

      trackVideo(video, mediaId) {
        if (this.videoMilestones.has(video)) return;

        const milestones = new Set();
        this.videoMilestones.set(video, milestones);

        video.addEventListener('play', () => {
          this.publishVideoEvent('video_start', video, mediaId, 0);
        });

        video.addEventListener('timeupdate', () => {
          if (!Number.isFinite(video.duration) || video.duration <= 0) return;

          const percent = Math.floor((video.currentTime / video.duration) * 100);
          [25, 50, 75].forEach((milestone) => {
            if (percent < milestone || milestones.has(milestone)) return;
            milestones.add(milestone);
            this.publishVideoEvent('video_progress', video, mediaId, milestone);
          });

          if (percent >= 99 && !milestones.has(100)) {
            milestones.add(100);
            this.publishVideoEvent('video_complete', video, mediaId, 100);
          }
        });

        video.addEventListener('ended', () => {
          if (milestones.has(100)) return;
          milestones.add(100);
          this.publishVideoEvent('video_complete', video, mediaId, 100);
        });

        if (!video.paused) {
          this.publishVideoEvent('video_start', video, mediaId, 0);
        }
      }

      publishVideoEvent(eventName, video, mediaId, percent) {
        const eventData = {
          video_current_time: Math.round(video.currentTime || 0),
          video_duration: Math.round(video.duration || 0),
          video_percent: percent,
          video_provider: 'shopify',
          video_title: video.getAttribute('aria-label') || video.getAttribute('title') || this.dataset.productTitle,
          video_url: video.currentSrc || video.src,
          video_media_id: mediaId.split('-').pop(),
          item_id: this.dataset.productId,
          item_name: this.dataset.productTitle,
          product_handle: this.dataset.productHandle,
          page_location: window.location.href,
          page_path: window.location.pathname,
        };

        if (window.Shopify?.analytics?.publish) {
          Shopify.analytics.publish(eventName, eventData);
        }

        if (typeof window.gtag === 'function') {
          window.gtag('event', eventName, eventData);
          return;
        }

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: eventName, ...eventData });
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
